import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Twitter, Facebook, Instagram, Youtube, ArrowLeft, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { DiamondLogo } from '../components/shared/DiamondLogo';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/site-settings/public`)
      .then(r => r.json())
      .then(setSettings)
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill out all fields');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) throw new Error((await r.json()).detail || 'Failed to send');
      setSent(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (err) {
      toast.error(err.message || 'Send failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-green-900 to-stone-900 p-4 md:p-8" data-testid="contact-page">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-amber-300 hover:text-amber-200 text-sm font-semibold mb-6" data-testid="contact-back-btn">
          <ArrowLeft size={14} /> Back to Diamond Scorer
        </button>

        <div className="flex items-center gap-3 mb-1">
          <DiamondLogo size={36} />
          <h1 className="text-3xl md:text-4xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            CONTACT US
          </h1>
        </div>
        <p className="text-green-100 text-base mb-8">
          Questions, feedback, or league partnership inquiries — we read every message.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Contact info card */}
          <div className="bg-white rounded-2xl shadow-xl p-5 space-y-4" data-testid="contact-info-card">
            <h2 className="text-lg font-bold text-stone-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>GET IN TOUCH</h2>

            {settings?.support_email && (
              <a href={`mailto:${settings.support_email}`} className="flex items-center gap-3 group" data-testid="contact-email-link">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0 group-hover:bg-amber-200 transition">
                  <Mail size={16} className="text-amber-700" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Email</p>
                  <p className="text-sm font-bold text-stone-800 group-hover:text-amber-700 transition">{settings.support_email}</p>
                </div>
              </a>
            )}
            {settings?.support_phone && (
              <a href={`tel:${settings.support_phone.replace(/[^\d+]/g,'')}`} className="flex items-center gap-3 group" data-testid="contact-phone-link">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0 group-hover:bg-emerald-200 transition">
                  <Phone size={16} className="text-emerald-700" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Phone</p>
                  <p className="text-sm font-bold text-stone-800 group-hover:text-emerald-700 transition">{settings.support_phone}</p>
                </div>
              </a>
            )}
            {settings?.business_address && (
              <div className="flex items-center gap-3" data-testid="contact-address">
                <div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center shrink-0">
                  <MapPin size={16} className="text-stone-700" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Location</p>
                  <p className="text-sm font-semibold text-stone-700">{settings.business_address}</p>
                </div>
              </div>
            )}

            {/* Social */}
            {(settings?.twitter_url || settings?.facebook_url || settings?.instagram_url || settings?.youtube_url) && (
              <div className="pt-2 border-t border-stone-100">
                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Follow</p>
                <div className="flex gap-2">
                  {settings.twitter_url && <a href={settings.twitter_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-700 transition" data-testid="contact-twitter"><Twitter size={14} /></a>}
                  {settings.facebook_url && <a href={settings.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-100 hover:bg-blue-100 hover:text-blue-700 flex items-center justify-center text-stone-700 transition" data-testid="contact-facebook"><Facebook size={14} /></a>}
                  {settings.instagram_url && <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-100 hover:bg-pink-100 hover:text-pink-700 flex items-center justify-center text-stone-700 transition" data-testid="contact-instagram"><Instagram size={14} /></a>}
                  {settings.youtube_url && <a href={settings.youtube_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-stone-100 hover:bg-red-100 hover:text-red-700 flex items-center justify-center text-stone-700 transition" data-testid="contact-youtube"><Youtube size={14} /></a>}
                </div>
              </div>
            )}
          </div>

          {/* Form card */}
          <div className="bg-white rounded-2xl shadow-xl p-5">
            {sent ? (
              <div className="text-center py-8" data-testid="contact-sent-confirmation">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 size={26} className="text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-stone-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>MESSAGE SENT</h2>
                <p className="text-stone-600 text-sm mt-1">Thanks! We&apos;ll reply within 24-48 hours.</p>
                <Button onClick={() => { setSent(false); setForm({ name: '', email: '', message: '' }); }} variant="outline" size="sm" className="mt-4" data-testid="contact-send-another">
                  Send another
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="contact-form">
                <h2 className="text-lg font-bold text-stone-800" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>SEND A MESSAGE</h2>
                <div>
                  <Label className="text-xs">Your name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required data-testid="contact-email-input" />
                </div>
                <div>
                  <Label className="text-xs">Message</Label>
                  <Textarea rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} maxLength={5000} required data-testid="contact-message-input" />
                </div>
                <Button type="submit" disabled={submitting} className="w-full bg-amber-500 hover:bg-amber-400 text-stone-900 font-bold" data-testid="contact-submit-btn">
                  <Send size={14} className="mr-1.5" /> {submitting ? 'Sending…' : 'Send Message'}
                </Button>
              </form>
            )}
          </div>
        </div>

        <p className="text-green-100/50 text-xs text-center mt-8">
          Diamond Scorer · <a href="/" className="hover:underline">diamondscorer.com</a>
        </p>
      </div>
    </div>
  );
}
